import { Code, type ConnectError } from '@connectrpc/connect'

/**
 * ConnectError が「ネットワーク接続に起因する技術的失敗」かどうかを判定する
 * (spec `docs/specs/plan-submit/plan-common.md` AC-PLC-023 の「ネットワーク
 * エラー」)。提出系画面 (`ProcurementPlanDetail` / `GenerationSalesPlanDetail`)
 * の submit mutation `onError` から共通利用する (issue #6915 / #6938 レビュー
 * 指摘 3901096374, 3901096482 対応)。
 *
 * 判定基準:
 * - `Code.Unavailable` / `Code.DeadlineExceeded`: protocol 層・サーバーが
 *   明示的に「通信不能」「タイムアウト」と分類したコード
 *   (`@connectrpc/connect` の `protocol/run-call.js` `setupSignal` は
 *   deadline 到達を `getAbortSignalReason` 経由で `Code.DeadlineExceeded` の
 *   `ConnectError` にする)。
 * - `Code.Unknown` かつ `cause instanceof TypeError`: **ブラウザの実際の
 *   回線断・DNS 失敗・CORS 拒否**は、fetch() が `TypeError` で reject する
 *   標準挙動 (MDN `WindowOrWorkerGlobalScope.fetch()` の仕様どおり)。
 *   `@connectrpc/connect-web` の fetch transport はこの reject を素通しし
 *   (try/catch で握り潰さない)、`@connectrpc/connect` の
 *   `protocol/run-call.js` `setupSignal` の `abort(reason)` が
 *   `ConnectError.from(reason)` を code 省略で呼ぶ。`connect-error.js` の
 *   `static from(reason, code = Code.Unknown)` は `reason instanceof Error`
 *   （AbortError/TimeoutError を除く）を `new ConnectError(reason.message,
 *   code, undefined, undefined, reason)` に変換するため、実通信断は
 *   **`code = Unknown` かつ `cause` に元の `TypeError` を保持した
 *   ConnectError** になる。AC-PLC-023 が指す「実通信断」の典型ケースは
 *   この経路のため、`Unavailable`/`DeadlineExceeded` だけでは検出漏れが
 *   起きる (issue #6938 review 3901096374)。
 *
 *   `Unknown` を丸ごと network 扱いにはしない: `Unknown` はサーバー側の
 *   未分類例外など fetch 失敗以外でも使われる汎用コードのため、
 *   `cause` が実際に `TypeError`（= fetch() 自身が reject した証跡）の
 *   場合のみ client-side の通信断とみなし、それ以外の `Unknown`
 *   （サーバー側の想定外エラー等）は従来どおり業務エラー扱い
 *   （raw `err.message` 表示）を維持する。
 */
export function isNetworkError(err: ConnectError): boolean {
  if (err.code === Code.Unavailable || err.code === Code.DeadlineExceeded) {
    return true
  }
  return err.code === Code.Unknown && err.cause instanceof TypeError
}
